/**
 * RecentProofStatusCard — calm "last proof upload" status surface.
 *
 * Operator brief (Feb 13, 2026): "After upload, learner sees status:
 * Awaiting analysis / Proof Verified / Building Proof / Not Yet Proven /
 * Awaiting Credits". This component does exactly that — fetches the
 * pupil's last few proof uploads for the current competency and
 * renders a single calm card for the most recent one, with a tiny
 * history list underneath.
 *
 * Renders nothing when the learner has not uploaded any proof yet
 * for this competency — keeps Training Mode calm.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  CheckCircle2, Loader2, Coins, AlertCircle, Circle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;


const PROOF_STATUS = {
  awaiting_analysis: {
    label: "Awaiting analysis",
    sub: "Your upload is in the 24-Hour AI pass queue.",
    Icon: Loader2,
    tone: "bg-indigo-50 text-indigo-900 border-indigo-200",
    spin: true,
  },
  awaiting_credits: {
    label: "Awaiting credits",
    sub: "Your upload is saved. Add proof credits to resume analysis.",
    Icon: Coins,
    tone: "bg-amber-50 text-amber-900 border-amber-300",
  },
  analysis_complete: {
    label: "Analysis complete",
    sub: "Your latest proof has been analysed — see the verdict below.",
    Icon: CheckCircle2,
    tone: "bg-emerald-50 text-emerald-900 border-emerald-200",
  },
};

const VERDICT_LABEL = {
  proof_verified:  { label: "AI observation · advisory", tone: "text-emerald-700" },
  building_proof:  { label: "Building advisory evidence", tone: "text-amber-700" },
  not_yet_proven:  { label: "More evidence needed", tone: "text-slate-600" },
};


export default function RecentProofStatusCard({ competencyId, refreshKey }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!competencyId) { setRows([]); return; }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/training/proof/recent/${competencyId}`,
          { withCredentials: true }
        );
        if (alive) setRows(r.data?.recent_uploads || []);
      } catch (_e) {
        if (alive) setRows([]);
      }
    })();
    return () => { alive = false; };
  }, [competencyId, refreshKey]);

  if (rows === null || rows.length === 0) return null;

  const latest = rows[0];
  const status = PROOF_STATUS[latest.proof_status] || PROOF_STATUS.awaiting_analysis;
  const { Icon } = status;
  const verdict = latest.latest_evidence?.verdict;
  const verdictMeta = verdict ? VERDICT_LABEL[verdict] : null;

  return (
    <div
      className={`rounded-2xl border-2 p-5 ${status.tone}`}
      data-testid="recent-proof-status-card"
      data-status={latest.proof_status}
    >
      <div className="flex items-start gap-3">
        <Icon
          size={22}
          className={`flex-shrink-0 ${status.spin ? "animate-spin" : ""}`}
        />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold tracking-[0.12em] opacity-70 mb-0.5">
            LAST PROOF UPLOAD
          </div>
          <div className="text-lg font-semibold leading-tight"
               data-testid="recent-proof-status-label">
            {status.label}
          </div>
          <div className="text-sm mt-1 opacity-90 leading-relaxed"
               data-testid="recent-proof-status-sub">
            {status.sub}
          </div>

          {verdictMeta && (
            <div className="mt-3 flex items-center gap-2"
                 data-testid="recent-proof-status-verdict">
              <Circle size={10} className={verdictMeta.tone} />
              <span className={`text-xs font-semibold ${verdictMeta.tone}`}>
                {verdictMeta.label}
              </span>
              {latest.latest_evidence?.retention_blocked && (
                <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wide">
                  · foundation slipped
                </span>
              )}
              {latest.latest_evidence?.variety_blocked && (
                <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">
                  · variety needed
                </span>
              )}
            </div>
          )}

          {latest.latest_evidence?.coaching && (
            <div className="text-xs italic opacity-80 mt-2 leading-relaxed"
                 data-testid="recent-proof-status-coaching">
              {latest.latest_evidence.coaching}
            </div>
          )}
        </div>
      </div>

      {rows.length > 1 && (
        <details className="mt-4 group"
                 data-testid="recent-proof-status-history-toggle">
          <summary className="cursor-pointer text-xs font-semibold opacity-70 hover:opacity-100">
            See earlier proof uploads ({rows.length - 1})
          </summary>
          <ul className="mt-2 space-y-1.5"
              data-testid="recent-proof-status-history">
            {rows.slice(1, 5).map((r, i) => {
              const s = PROOF_STATUS[r.proof_status] || PROOF_STATUS.awaiting_analysis;
              const v = r.latest_evidence?.verdict
                ? VERDICT_LABEL[r.latest_evidence.verdict]?.label
                : null;
              return (
                <li
                  key={r.id || i}
                  className="flex items-center justify-between gap-2 text-xs py-1 border-t border-current/10 pt-1.5"
                  data-testid={`recent-proof-status-history-row-${i}`}
                  data-status={r.proof_status}
                >
                  <span className="font-medium opacity-80">{s.label}</span>
                  <span className="opacity-70">{v || "—"}</span>
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </div>
  );
}
